import { createStyles, Flex, Group, Menu, ScrollArea, Text, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from 'react-responsive';
import { type ReactNode, useState } from 'react';
import { IconChevronDown, IconEdit, IconOutbound, IconPencil } from '@tabler/icons-react';

import { UnstyledNextLink } from '~/components/shared/UnstyledNextLink/UnstyledNextLink';
import dt from '~/testing';

import { EntryWithdrawModal } from '../Modal/EntryWithdrawModal';

const useStyles = createStyles((theme) => ({
  target: {
    display: 'flex',
    cursor: 'pointer',
    gap: 4,
    justifyContent: 'space-between',
    flexGrow: 1,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  bottomDivider: {
    '&:not(:last-child)': {
      borderBottom: `1px solid ${theme.colors.gray[2]}`,
      paddingBottom: `${theme.spacing.md}px`,
      marginBottom: `${theme.spacing.md}px`,
    },
  },
}));

type EntryRowProps = {
  children: ReactNode;
  entryId: string;
  name: string;
  contestId: string;
  options?: {
    editName?: {
      title: string;
      onClick: () => void;
    };
    edit?: {
      title: string;
      href?: string;
      isHidden?: boolean;
    };
    withdrawal?: {
      title: string;
      isHidden?: boolean;
    };
  };
  isEntertainmentOnly?: boolean;
};

function EntryRow({
  entryId,
  name,
  children,
  contestId,
  options,
  isEntertainmentOnly,
}: EntryRowProps) {
  const theme = useMantineTheme();
  const { classes } = useStyles();
  const isTablet = useMediaQuery({ maxWidth: theme.breakpoints.lg });

  const [isEntryWithdrawModalOpened, setIsEntryWithdrawModalOpened] = useState(false);

  const { edit, editName, withdrawal } = options || {};

  return (
    <Flex
      direction={isTablet ? 'column' : 'row'}
      justify="space-between"
      align="stretch"
      gap={theme.spacing.xl}
      w="100%"
      className={classes.bottomDivider}
      data-test-id={dt.contestDetail.section.myEntries.row.container}
    >
      <Flex justify="space-between" align="center" w="100%" maw={84}>
        <Menu shadow="md" width={200} position="bottom-start">
          <Menu.Target>
            <Text
              className={classes.target}
              size={14}
              weight={700}
              data-test-id={dt.contestDetail.section.myEntries.row.title}
            >
              {name}
              {(edit?.isHidden && withdrawal.isHidden) ||
                (options && <IconChevronDown size={20} />)}
            </Text>
          </Menu.Target>

          {(edit?.isHidden && withdrawal.isHidden) ||
            (options && (
              <Menu.Dropdown
                data-test-id={dt.contestDetail.section.myEntries.row.options.container}
              >
                {!edit?.isHidden && (
                  <UnstyledNextLink
                    href={edit?.href}
                    data-test-id={dt.contestDetail.section.myEntries.row.options.edit.link}
                  >
                    <Menu.Item
                      icon={<IconEdit size={theme.spacing.lg} />}
                      data-test-id={dt.contestDetail.section.myEntries.row.options.edit.text}
                    >
                      {edit.title}
                    </Menu.Item>
                  </UnstyledNextLink>
                )}
                {!!editName && (
                  <Menu.Item
                    icon={<IconPencil size={theme.spacing.lg} />}
                    data-test-id={dt.contestDetail.section.myEntries.row.options.withdrawal.text}
                    onClick={editName.onClick}
                  >
                    {editName.title}
                  </Menu.Item>
                )}
                {!withdrawal?.isHidden && (
                  <Menu.Item
                    icon={<IconOutbound size={theme.spacing.lg} />}
                    data-test-id={dt.contestDetail.section.myEntries.row.options.withdrawal.text}
                    onClick={() => setIsEntryWithdrawModalOpened(true)}
                  >
                    {withdrawal?.title}
                  </Menu.Item>
                )}
              </Menu.Dropdown>
            ))}
        </Menu>
      </Flex>
      <ScrollArea type="never" mx={`-${theme.spacing.lg}px`}>
        <Group
          noWrap
          px={theme.spacing.lg}
          spacing={4}
          data-test-id={dt.contestDetail.section.myEntries.row.items}
        >
          {children}
        </Group>
      </ScrollArea>
      <EntryWithdrawModal
        entryId={entryId}
        contestId={contestId}
        isEntertainmentOnly={isEntertainmentOnly}
        isOpen={isEntryWithdrawModalOpened}
        setModal={setIsEntryWithdrawModalOpened}
      />
    </Flex>
  );
}

export default EntryRow;
